/**
 * Plain-language publish failures.
 *
 * CONTRACT (cross-platform lane, 2026-11-04; documented in
 * `docs/platform-apis/MORA-PUBLISH-PIPELINE.md` "Shared contracts"):
 *
 *   publishFailureMessage(platform, rawError) -> { message, detail, code }
 *
 * - `message` is one plain sentence a marketer can act on. Sentence case, no
 *   provider jargon, no JSON, no status codes, no em dashes.
 * - `detail` is the raw provider text, kept separately so the editor can show it
 *   behind a "Details" link and support can read it. It is never part of `message`.
 * - `code` is a stable snake_case id for the matched rule (`unknown` when nothing
 *   matched), so callers can branch on it and tests can pin it.
 *
 * It accepts whatever reaches it: a string (`Platform error: Request failed with
 * status 400: {"error":{...}}`), an `Error`, a parsed provider body, or null.
 * The matching is ordered, per platform, then shared rules, then a status-code
 * fallback, then a generic sentence. Platform lanes add a rule by appending one
 * entry to their platform's list below; keep each entry on its own lines so
 * parallel edits merge cleanly.
 *
 * Lives in `shared/` (not `supabase/functions/_shared/`) so the post editor and the
 * edge functions use the same sentences. Pure: no I/O, no Deno or DOM APIs.
 */

import {
  INSTAGRAM_ERROR_MESSAGES,
  INSTAGRAM_PROCESSING_TIMEOUT_MESSAGE,
  INSTAGRAM_STILL_PROCESSING_MESSAGE,
} from './instagram-container-status.ts';
import { tiktokPlainSentences } from './tiktok-errors.ts';

export type PublishFailurePlatform =
  'instagram' | 'facebook' | 'linkedin' | 'tiktok' | 'threads' | 'twitter' | string;

export interface PublishFailureMessage {
  /** One plain sentence. Safe to show as the failed-post line. */
  message: string;
  /** The raw provider text, or null when there was none. Show behind "Details". */
  detail: string | null;
  /** Stable id of the matched rule, `unknown` when none matched. */
  code: string;
}

interface FailureRule {
  code: string;
  /** Tested against the raw text lower-cased, and against extracted provider codes. */
  match: RegExp;
  message: string;
}

const GENERIC =
  'The post could not be published. Try again, or open Details for what the platform said.';

/** Rules shared by every platform, applied after the platform's own list. */
const SHARED_RULES: FailureRule[] = [
  {
    code: 'token_expired',
    match:
      /(token.*(expired|invalid|revoked)|invalid_?access_?token|oauthexception.*190|error_subcode.?4(63|67)|unauthorized_client|revoked_access_token)/i,
    message:
      'The connection to this account has expired. Reconnect it in Settings, then try again.',
  },
  {
    code: 'rate_limited',
    match:
      /(rate.?limit|too many requests|status 429|throttl|spam_risk_too_many_actions|request limit reached|daily.*limit)/i,
    message: 'The platform has paused posting for now because of its limits. Try again later.',
  },
  {
    code: 'timeout_unknown',
    match:
      /(timeout invoking|did not complete and cannot be safely retried|may have already (created|published)|accepted the request but returned no post id)/i,
    message:
      'We could not confirm whether this post went out. Check the platform before posting again.',
  },
  {
    code: 'media_download_failed',
    match:
      /(media download has failed|could not (download|fetch)|source fetch failed|unable to (download|fetch)|url_ownership_unverified|file_format_check_failed)/i,
    message: 'The platform could not read the image or video file. Re-upload it and try again.',
  },
];

/** Sentences Mora's own Instagram and Facebook publishers already write in plain words; they pass through. */
function plainSentenceRule(code: string, message: string): FailureRule {
  const start = message.slice(0, 48).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return { code, match: new RegExp(start, 'i'), message };
}

const INSTAGRAM_RULES: FailureRule[] = [
  ...INSTAGRAM_ERROR_MESSAGES.map((entry) =>
    plainSentenceRule(`ig_plain_${entry.code}`, entry.message),
  ),
  plainSentenceRule('ig_still_processing', INSTAGRAM_STILL_PROCESSING_MESSAGE),
  plainSentenceRule('ig_processing_timeout', INSTAGRAM_PROCESSING_TIMEOUT_MESSAGE),
  {
    code: 'ig_quota_spent',
    match: /posts through the API in \d+ hours, and this account has used them all/i,
    message:
      'This Instagram account has used all the posts Instagram allows through apps in 24 hours. Try again later.',
  },
  {
    code: 'ig_carousel_video_pending',
    match: /still processing one of the videos in the carousel/i,
    message:
      'Instagram is still processing one of the videos in the carousel. Try again in a few minutes.',
  },
  {
    code: 'ig_media_format',
    match:
      /(2207005|2207026|only photo or video can be accepted|unsupported.*(format|media)|media type.*not supported)/i,
    message: 'Instagram does not accept this file. Use a JPEG image, or an MP4 or MOV video.',
  },
  {
    code: 'ig_aspect_ratio',
    match: /(2207009|aspect ratio|ratio.*(not|isn.t).*supported)/i,
    message: 'Instagram cannot show this image at its shape. Crop it between 4:5 and 1.91:1.',
  },
  {
    code: 'ig_video_length',
    match: /(video.*(too (long|short)|duration)|duration.*(too (long|short)|limit))/i,
    message:
      'Instagram does not accept a video of this length. Reels run from 3 seconds to 15 minutes.',
  },
  {
    code: 'ig_processing_error',
    match:
      /(2207001|2207003|2207008|status_code.?error|container.*(error|expired)|processing.*(failed|error))/i,
    message:
      'Instagram could not process this media. Try a different file, or export the video again.',
  },
  {
    code: 'ig_caption_too_long',
    match: /(2207010|caption.*(too long|exceed|limit))/i,
    message:
      'The caption is longer than Instagram allows. Shorten it to 2,200 characters or fewer.',
  },
  {
    code: 'ig_publish_limit',
    match:
      /(2207042|content_publishing_limit|maximum.*posts.*(24|day)|reached.*(limit|maximum).*(posts|publishing))/i,
    message: 'This Instagram account has reached its daily posting limit. Try again tomorrow.',
  },
  {
    code: 'ig_account_type',
    match: /(business or creator|professional account|account.*not.*(business|creator))/i,
    message: 'Instagram only allows this from a Business or Creator account.',
  },
  // Every other documented subcode reads from the one table in instagram-container-status.ts
  // (Meta's error code page, read 4 Nov 2026), so the editor and the publisher cannot disagree.
  ...INSTAGRAM_ERROR_MESSAGES.map((entry) => ({
    code: `ig_${entry.code}`,
    match: new RegExp(`\\b${entry.code}\\b`),
    message: entry.message,
  })),
];

const FACEBOOK_RULES: FailureRule[] = [
  {
    code: 'fb_reel_ratio',
    match: /1363040/,
    message: 'Facebook Reels must be 9:16 (vertical). Crop or re-export the video and try again.',
  },
  {
    code: 'fb_reel_resolution',
    match: /1363127/,
    message: 'The video is smaller than Facebook Reels allow. Use at least 540 by 960 pixels.',
  },
  {
    code: 'fb_reel_duration',
    match: /1363128/,
    message: 'Facebook Reels run from 3 to 90 seconds. Trim or extend the video and try again.',
  },
  {
    code: 'fb_reel_frame_rate',
    match: /1363129/,
    message: 'Facebook Reels need a fixed 24 to 60 frames per second. Export the video again.',
  },
  {
    code: 'fb_reel_upload',
    match:
      /(did not open an upload session|could not fetch the video from its link|could not process the video)/i,
    message:
      'Facebook could not take this video. Check the file is still available and is an MP4, then try again.',
  },
  {
    code: 'fb_permission',
    match:
      /(\(#200\)|\(#10\)|code.?200|permission.*(denied|missing|required)|pages_manage_posts|pages_read_engagement)/i,
    message:
      'Mora does not have permission to post to this Facebook Page. Reconnect the Page and try again.',
  },
  {
    code: 'fb_duplicate',
    match: /(duplicate status|you.*already posted|identical.*post|1346003|1404006)/i,
    message:
      'Facebook rejected this as a duplicate of a recent post. Change the text and try again.',
  },
  {
    code: 'fb_photo_format',
    match:
      /(1366046|photo.*(format|type|invalid)|invalid.*(image|photo)|unsupported.*image|file type.*not (supported|allowed))/i,
    message: 'Facebook does not accept this image file. Use a JPEG or PNG under 10 MB.',
  },
  {
    code: 'fb_video_processing',
    match:
      /(video.*(processing|upload).*(fail|error)|1363\d{3}|video file.*(invalid|corrupt)|unsupported video)/i,
    message: 'Facebook could not process this video. Try an MP4 under 4 GB.',
  },
  {
    code: 'fb_link_blocked',
    match: /(1346\d{3}|link.*(blocked|spam|violat)|url.*(blocked|not allowed))/i,
    message: 'Facebook blocked a link in this post. Remove the link or use a different one.',
  },
];

const LINKEDIN_RULES: FailureRule[] = [
  // LinkedIn names the field in the message ("fields [/content/article/description]"), so these come first and say which.
  {
    code: 'li_link_text_too_long',
    match:
      /FIELD_LENGTH_TOO_LONG[\s\S]*(article|description|\/title)|(description|\/title)[\s\S]*FIELD_LENGTH_TOO_LONG/i,
    message:
      'The link title or description is longer than LinkedIn allows. Shorten it and try again.',
  },
  {
    code: 'li_alt_text_too_long',
    match: /FIELD_LENGTH_TOO_LONG[\s\S]*alt|alt.?text[\s\S]*FIELD_LENGTH_TOO_LONG/i,
    message:
      'An image description (alt text) is longer than LinkedIn allows. Shorten it to 4,086 characters or fewer.',
  },
  {
    code: 'li_text_too_long',
    match: /FIELD_LENGTH_TOO_LONG|commentary.*(too long|exceed)/i,
    message:
      'The text is longer than LinkedIn allows. Shorten the post to 3,000 characters or fewer.',
  },
  {
    code: 'li_access_denied',
    match: /ACCESS_DENIED|not enough permissions|w_organization_social|w_member_social|status 403/i,
    message:
      'LinkedIn says this account cannot post here. Reconnect it, and check you are an admin of the Page.',
  },
  {
    code: 'li_invalid_urn',
    match: /INVALID_URN_TYPE|invalid urn|urn:li:.*(not found|invalid)/i,
    message:
      'LinkedIn could not find the account or Page to post as. Reconnect the account and try again.',
  },
  {
    code: 'li_duplicate',
    match: /duplicate.*(post|content|share)|DUPLICATE_POST|content is a duplicate/i,
    message:
      'LinkedIn rejected this as a duplicate of a recent post. Change the text and try again.',
  },
  {
    code: 'li_image_upload',
    match:
      /(image.*(upload|processing).*(fail|error)|initializeUpload|unsupported.*image|image.*(too (large|big)|pixels))/i,
    message: 'LinkedIn could not take this image. Use a JPEG, PNG or GIF.',
  },
  {
    code: 'li_video',
    match:
      /(video.*(upload|processing).*(fail|error)|could not process this video|finalizeUpload|\/videos\b)/i,
    message:
      'LinkedIn could not process this video. Check that it is an MP4 between 3 seconds and 30 minutes, then try again.',
  },
  {
    code: 'li_document',
    match:
      /(document.*(upload|processing).*(fail|error)|could not process this document|\/documents\b|still preparing this document)/i,
    message:
      'LinkedIn could not take this document. Check that it is a PDF, PowerPoint or Word file under 100 MB and 300 pages, then try again.',
  },
  {
    code: 'li_daily_budget',
    match: /LinkedIn requests( and this account|,? more than)|daily limit of LinkedIn requests/i,
    message:
      'Mora held this post back to stay within the number of LinkedIn requests allowed in a day. It can go out tomorrow, or use fewer images.',
  },
  {
    code: 'li_mention',
    match: /(mention|little.?text).*(invalid|fail|not found)|organization names must match/i,
    message:
      'A company tag in this post does not match a LinkedIn Page. Check the name or remove the tag.',
  },
];

const escapeForRegExp = (value: string): RegExp =>
  new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

// `post-to-tiktok` already turns TikTok's documented error codes and fail_reason values into plain
// sentences (shared/tiktok-errors.ts). They reach this mapper as "Platform error: <sentence>", so a
// sentence matches itself and is shown as written instead of falling to the generic line.
const TIKTOK_PLAIN_RULES: FailureRule[] = tiktokPlainSentences().map(({ code, message }) => ({
  code,
  match: escapeForRegExp(message),
  message,
}));

const TIKTOK_RULES: FailureRule[] = [
  ...TIKTOK_PLAIN_RULES,
  {
    code: 'tt_unaudited_private',
    match: /unaudited_client_can_only_post_to_private_accounts/i,
    message:
      'TikTok is limiting this app to private posts until it is approved. The post can only be private for now.',
  },
  {
    code: 'tt_privacy_mismatch',
    match: /privacy_level_option_mismatch/i,
    message:
      'TikTok does not allow that visibility for this account. Pick another option and try again.',
  },
  {
    code: 'tt_url_unverified',
    match: /url_ownership_unverified|url_prefix.*(unverified|not verified)/i,
    message:
      'TikTok could not confirm the image address. This is a setup issue on our side, not yours.',
  },
  {
    code: 'tt_frame_rate',
    match: /frame_rate_check_failed/i,
    message: 'TikTok needs a video between 23 and 60 frames per second. Export it again at 30 fps.',
  },
  {
    code: 'tt_picture_size',
    match: /picture_size_check_failed/i,
    message: 'TikTok could not use one of the photos. Use JPEG images up to 1080 pixels and 20 MB.',
  },
  {
    code: 'tt_duration',
    match: /duration_check_failed|max_video_post_duration/i,
    message: 'This video is longer than your TikTok account allows. Trim it and try again.',
  },
  {
    code: 'tt_file_format',
    match: /file_format_check_failed|video_pull_failed|invalid_file_upload/i,
    message: 'TikTok could not read this file. Use an MP4 video or JPEG photos.',
  },
  {
    code: 'tt_spam',
    match: /spam_risk|user_banned_from_posting|reached_active_user_cap|daily_post/i,
    message: 'TikTok has stopped new posts from this account for now. Try again later.',
  },
  {
    code: 'tt_processing',
    match: /tiktok processing failed|status.*FAILED/i,
    message: 'TikTok could not finish processing this post. Try again, or try a different file.',
  },
  {
    code: 'tt_scope',
    match: /scope_not_authorized|scope_permission_missed/i,
    message: 'Mora is missing a TikTok permission. Reconnect TikTok and try again.',
  },
];

const PLATFORM_RULES: Record<string, FailureRule[]> = {
  instagram: INSTAGRAM_RULES,
  facebook: FACEBOOK_RULES,
  linkedin: LINKEDIN_RULES,
  tiktok: TIKTOK_RULES,
};

function rawToText(rawError: unknown): string {
  if (rawError == null) return '';
  if (typeof rawError === 'string') return rawError;
  if (rawError instanceof Error) return rawError.message;
  try {
    return JSON.stringify(rawError);
  } catch {
    return String(rawError);
  }
}

/** Provider codes and sentences found inside embedded JSON, so rules can match them by name. */
function embeddedProviderText(text: string): string {
  const start = text.indexOf('{');
  if (start === -1) return '';
  try {
    const parsed = JSON.parse(text.slice(start)) as Record<string, unknown>;
    const out: string[] = [];
    const walk = (value: unknown, depth: number): void => {
      if (depth > 4 || value == null) return;
      if (typeof value === 'string' || typeof value === 'number') {
        out.push(String(value));
        return;
      }
      if (Array.isArray(value)) {
        for (const item of value) walk(item, depth + 1);
        return;
      }
      if (typeof value === 'object') {
        for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
          if (
            /^(code|error_subcode|message|error_user_msg|error_user_title|serviceErrorCode|status|fail_reason|log_id)$/.test(
              key,
            )
          ) {
            out.push(`${key}=`);
          }
          walk(nested, depth + 1);
        }
      }
    };
    walk(parsed, 0);
    return out.join(' ');
  } catch {
    return '';
  }
}

/** The raw text with the app's own wrappers removed, so Details reads as the provider said it. */
function stripWrappers(text: string): string {
  return text
    .replace(/^\s*(Platform error|Function invocation failed):\s*/i, '')
    .replace(/^\s*Request failed with status (\d{3}):\s*/i, 'Status $1: ')
    .trim();
}

/** A bare HTTP status is the last hint before the generic sentence. */
function statusOf(text: string): number | null {
  const match = text.match(/status (\d{3})\b/i);
  return match ? Number(match[1]) : null;
}

export function publishFailureMessage(
  platform: PublishFailurePlatform,
  rawError: unknown,
): PublishFailureMessage {
  const text = rawToText(rawError).trim();
  if (!text) return { message: GENERIC, detail: null, code: 'unknown' };

  const searchable = `${text}\n${embeddedProviderText(text)}`;
  const platformRules = PLATFORM_RULES[String(platform).toLowerCase()] ?? [];
  for (const rule of [...platformRules, ...SHARED_RULES]) {
    if (rule.match.test(searchable)) {
      return { message: rule.message, detail: stripWrappers(text), code: rule.code };
    }
  }

  const status = statusOf(text);
  if (status === 401) {
    return {
      message: 'The connection to this account is no longer valid. Reconnect it and try again.',
      detail: stripWrappers(text),
      code: 'http_401',
    };
  }
  if (status === 403) {
    return {
      message:
        'The platform refused this post for this account. Reconnect the account, or check its permissions.',
      detail: stripWrappers(text),
      code: 'http_403',
    };
  }
  if (status !== null && status >= 500) {
    return {
      message: 'The platform had a problem on its side. Wait a few minutes and try again.',
      detail: stripWrappers(text),
      code: 'http_5xx',
    };
  }
  if (status === 400) {
    return {
      message:
        'The platform rejected this post. Check the text and media against its limits, or open Details.',
      detail: stripWrappers(text),
      code: 'http_400',
    };
  }

  return { message: GENERIC, detail: stripWrappers(text), code: 'unknown' };
}

/** The error object a provider returned, reduced to the fields that matter. */
export interface ProviderErrorInfo {
  /** HTTP status of the provider response. */
  status: number;
  /** Provider's own code: Meta `code`, LinkedIn `code`, TikTok `error.code`. */
  code: string | null;
  /** Meta `error_subcode`. */
  subcode: string | null;
  /** The provider's technical message. */
  message: string | null;
  /** A sentence the provider wrote for end users (Meta `error_user_msg`), when it sent one. */
  userMessage: string | null;
}

/**
 * Reduce a provider error body to {@link ProviderErrorInfo}. Used by the API client base
 * class so every thrown `PostingError` carries the same shape in `details.provider`,
 * whatever the platform. Returns null for a body that is not JSON.
 */
export function parseProviderError(status: number, body: string): ProviderErrorInfo | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const root = parsed as Record<string, unknown>;
  const inner =
    root.error && typeof root.error === 'object' ? (root.error as Record<string, unknown>) : root;
  const pick = (value: unknown): string | null =>
    typeof value === 'string' && value ? value : typeof value === 'number' ? String(value) : null;
  return {
    status,
    code: pick(inner.code) ?? pick(root.serviceErrorCode) ?? pick(root.code),
    subcode: pick(inner.error_subcode),
    message:
      pick(inner.message) ??
      pick(root.message) ??
      (typeof root.error === 'string' ? root.error : null),
    userMessage: pick(inner.error_user_msg),
  };
}

/**
 * A readiness or app-side message (already plain: "Slide 2 could not be prepared for
 * instagram.") is not a provider failure. Callers that cannot tell the two apart can
 * pass the text here: it returns true when the text already reads as a sentence the
 * user can act on, so the editor does not bury it behind a generic line.
 */
export function isAlreadyPlainFailure(rawError: unknown): boolean {
  const text = rawToText(rawError).trim();
  if (!text || text.length > 220) return false;
  if (
    /[{}]|status \d{3}|error_subcode|OAuthException|Platform error:|Function invocation/i.test(text)
  ) {
    return false;
  }
  return /[.!]$/.test(text) && /\s/.test(text);
}
